import { useEffect, useRef, useState, type CSSProperties } from 'react';

export interface LogoBackgroundRemovalResult {
  /** Transparent PNG as a data URL. */
  image: string;
  method?: string;
}

interface LogoBackgroundRemovalModalProps {
  open: boolean;
  /** The logo as it is now (shown as "Original"). */
  originalUrl: string;
  /** Runs the removal (one paid call) — the caller picks the endpoint. */
  removeBackground: () => Promise<LogoBackgroundRemovalResult>;
  /** "Use transparent logo": the PNG as a File plus an object URL for previews. */
  onUse: (file: File, previewUrl: string) => void | Promise<void>;
  /** "Keep original" / close. */
  onClose: () => void;
  /** Shown as one of the preview backgrounds. */
  brandColor?: string | null;
  accent?: 'blue' | 'teal';
}

const CHECKERBOARD = 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 16px 16px';
const BUSY_BACKGROUND = 'linear-gradient(135deg, #f97316 0%, #ec4899 35%, #6366f1 70%, #0ea5e9 100%)';

const ACCENT = {
  blue: 'bg-[#0859B8] hover:bg-[#06428B] disabled:bg-[#0859B8]/40',
  teal: 'bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300',
};

function errorMessage(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return err?.message || "The background couldn't be removed. Please try again.";
}

/**
 * "Remove background" for the brand logo: runs the removal once when opened,
 * then shows the result on several backgrounds so the user can judge it before
 * choosing "Use transparent logo" or "Keep original". Nothing is saved here —
 * onUse hands the PNG back to the screen's normal logo upload.
 */
export default function LogoBackgroundRemovalModal({
  open,
  originalUrl,
  removeBackground,
  onUse,
  onClose,
  brandColor,
  accent = 'blue',
}: LogoBackgroundRemovalModalProps) {
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [using, setUsing] = useState(false);
  const runId = useRef(0);

  useEffect(() => {
    if (!open) return;
    const id = ++runId.current;
    setResult(null);
    setError(null);
    setUsing(false);
    removeBackground()
      .then((res) => {
        if (runId.current === id) setResult(res.image);
      })
      .catch((err) => {
        if (runId.current === id) setError(errorMessage(err));
      });
    return () => {
      runId.current++; // ignore a late response after closing
    };
  }, [open]);

  if (!open) return null;

  const handleUse = async () => {
    if (!result) return;
    setUsing(true);
    try {
      const blob = await (await fetch(result)).blob();
      const file = new File([blob], 'logo.png', { type: 'image/png' });
      await onUse(file, URL.createObjectURL(blob));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUsing(false);
    }
  };

  const backgrounds: { label: string; style: CSSProperties }[] = [
    { label: 'Transparent', style: { background: CHECKERBOARD } },
    { label: 'White', style: { background: '#ffffff' } },
    { label: 'Dark', style: { background: '#111111' } },
    ...(brandColor ? [{ label: 'Brand colour', style: { background: brandColor } }] : []),
    { label: 'Busy image', style: { background: BUSY_BACKGROUND } },
  ];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logo-bg-removal-title"
        className="bg-white rounded-[20px] w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 id="logo-bg-removal-title" className="font-black text-[#222222]">Remove logo background</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="flex items-start gap-4">
            <div className="shrink-0 text-center">
              <div className="w-28 h-28 rounded-xl border border-gray-200 bg-white flex items-center justify-center p-2">
                <img src={originalUrl} alt="Original logo" className="max-w-full max-h-full object-contain" />
              </div>
              <p className="mt-1.5 text-[11px] font-medium text-gray-500">Original</p>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed pt-1">
              We remove the plain background around your logo and keep its colours and edges exactly as they
              are. Your original logo is kept too — you choose which one to use on each post.
            </p>
          </div>

          {!result && !error && (
            <div className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-6 text-sm text-gray-600">
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              <span>Removing the background — this takes about 10 seconds…</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
              <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Background removed</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {backgrounds.map(({ label, style }) => (
                  <div key={label} className="text-center">
                    <div
                      className="aspect-square rounded-xl border border-gray-200 flex items-center justify-center p-2"
                      style={style}
                    >
                      <img src={result} alt={`Logo on ${label.toLowerCase()} background`} className="max-w-full max-h-full object-contain" />
                    </div>
                    <p className="mt-1.5 text-[11px] font-medium text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            Keep original
          </button>
          <button
            type="button"
            onClick={handleUse}
            disabled={!result || using}
            className={`px-5 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center gap-2 ${ACCENT[accent]}`}
          >
            {using && <i className="ri-loader-4-line animate-spin"></i>}
            Use transparent logo
          </button>
        </div>
      </div>
    </div>
  );
}
